<template>
  <div :class="$style.root">
    <LayoutCollectionTypeHeadingComponent
      :pageData="pageData"
      :parentPageData="parentPageData"
      routeName="program-slug"
      parentRouteName="program"
    />

    <div :class="$style.information">
      <div :class="$style.dates">
        <div>
          <div v-if="tag" :class="$style.tag">{{ tag }}</div>
          <CoreDateTimeRangeComponent
            :class="$style.dateTimeRange"
            v-for="range in localizedData?.date_time_ranges"
            :key="range?.id"
            :dateStart="range?.date_time_start"
            :dateEnd="range?.date_time_end"
          />
        </div>

        <div
          v-if="localizedData?.opening_date_time?.date_time_start && localizedData?.opening_date_time?.date_time_end"
        >
          <div :class="$style.tag">{{ $t('program.openingEvent') }}</div>
          <CoreDateTimeRangeComponent
            :class="$style.dateTimeRange"
            :key="localizedData?.opening_date_time?.id"
            :dateStart="localizedData?.opening_date_time?.date_time_start"
            :dateEnd="localizedData?.opening_date_time?.date_time_end"
          />
        </div>
      </div>

      <CoreTextComponent
        v-if="introduction"
        :class="$style.introduction"
        :string="introduction"
      />

      <CoreImageComponent
        v-if="localizedData?.cover_image?.data?.attributes"
        :image="localizedData?.cover_image?.data?.attributes"
        :class="$style.image"
      />
    </div>

    <CoreBlocksComponent
      v-if="blocks?.length"
      :blocks="blocks"
    />

    <div
      v-if="localizedData?.program_entries?.data?.length"
      :class="$style.programEntries"
    >
      <div :class="$style.programEntriesLabel">
        {{ $t('shared.relatedTo', { name: title }) }}
      </div>

      <SharedThumbnailsProgramsComponent
        :thumbnails="localizedData?.program_entries?.data"
        :includeRelated="false"
      />
    </div>

    <div
      v-if="localizedData?.program_entry?.data"
      :class="$style.programEntries"
    >
      <div :class="$style.programEntriesLabel">
        {{ $t('shared.partOf', { origin: title, source: getProgramEntryTitle(localizedData?.program_entry?.data) }) }}
      </div>
      <SharedThumbnailsProgramsComponent
        :thumbnails="[localizedData?.program_entry?.data]"
        :includeRelated="false"
      />
    </div>
  </div>
</template>

<script setup>
const props = defineProps({
  pageData: Object,
  parentPageData: Object
})

const localizedData = computed(() => {
  return i18nGetLocalizedData(baseStrapiGetAttributes(props?.pageData))
})

const blocks = computed(() => {
  return localizedData?.value?.blocks
})

const title = computed(() => {
  return localizedData?.value?.titles?.current_locale || localizedData?.value?.titles?.i18n_text_en
})

const introduction = computed(() => {
  return localizedData?.value?.introductions?.current_locale || localizedData?.value?.introductions?.i18n_richtext_en
})

const tag = computed(() => {
  return localizedData?.value?.tags?.current_locale || localizedData?.value?.tags?.i18n_text_en
})

const getProgramEntryTitle = (entry) => {
  const localizedData  = i18nGetLocalizedData(baseStrapiGetAttributes(entry))
  return localizedData?.titles?.current_locale || localizedData?.titles?.i18n_text_en
}
</script>

<style module lang=scss>
.root {}

.information {
  display: grid;
  grid-template-columns: 1fr 1fr 2fr;
  grid-template-areas: 'dates introduction image';
  align-items: end;
  grid-column-gap: var(--unit--horizontal--);

  margin-bottom: var(--unit--vertical--sextuple);
  margin-left: auto;
  margin-right: auto;

  padding-left: var(--unit--horizontal--tripple);
  padding-right: var(--unit--horizontal--tripple);

  max-width: var(--container--large);
  width: 100%;

  @media (max-width: $breakpoint-m) {
    grid-column-gap: 0;
    grid-row-gap: var(--unit--vertical--double);

    grid-template-columns: 1fr 1fr;
    grid-template-areas: 'image image'
                         'dates introduction';
  }

  @media (max-width: $breakpoint-s) {
    grid-column-gap: 0;
    grid-row-gap: var(--unit--vertical--double);
    grid-template-columns: 1fr;
    grid-template-areas: 'image'
                         'dates'
                         'introduction';
  }
}

.dates {
  grid-area: dates;

  display: flex;
  flex-direction: column;
  gap: var(--unit--vertical);
}

.dates time {
  display: inline-block;
}

.introduction {
  grid-area: introduction;
}

.image {
  grid-area: image;
}

.tag {
  text-transform: uppercase;
}

.dateTimeRange {
  display: block;
}

.programEntries {
  margin-top: var(--unit--vertical--sextuple);

  display: flex;
  flex-direction: column;
  gap: var(--unit--vertical--tripple);
}

.programEntriesLabel {
  text-align: center;
}
</style>
